import { type QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import type React from 'react'
import { createContext, useContext } from 'react'
import { isNil } from 'utils'
import type { ApiClient } from 'utils/ApiClient'

type ApiProviderProps = {
  children: React.ReactNode
  apiClient: ApiClient
  queryClient: QueryClient
  showDevTools?: boolean
}

type ApiContext = {
  apiClient: ApiClient
  queryClient: QueryClient
}

const ApiContext = createContext<ApiContext | null>(null)

/**
 * Provides the api client and query client to child components.
 * Without this, useApiClient and useQueryClient will throw an error,
 * as will all hooks related to react-query.
 */
export const ApiProvider: React.FC<ApiProviderProps> = (props) => {
  const { children, showDevTools = false, ...value } = props

  return (
    <ApiContext.Provider value={value}>
      <QueryClientProvider client={props.queryClient}>
        {showDevTools && <ReactQueryDevtools initialIsOpen={false} />}
        {children}
      </QueryClientProvider>
    </ApiContext.Provider>
  )
}

/**
 * Access the api client from within an ApiProvider child component.
 * The api client provides methods for making http requests to the
 * app's canonical backend.
 */
export const useApiClient = () => {
  const context = useContext(ApiContext)

  if (isNil(context)) {
    throw new Error('useApiClient must be used within an ApiProvider')
  }

  return context.apiClient
}

/**
 * Access the query client from within an ApiProvider child component
 * See react-query's QueryClient documentation for more information:
 * https://tanstack.com/query/latest/docs/reference/QueryClient
 */
export const useQueryClient = () => {
  const context = useContext(ApiContext)

  if (isNil(context)) {
    throw new Error('useQueryClient must be used within an ApiProvider')
  }

  return context.queryClient
}
